import {
  CustomFieldWithValue,
  MultiOptionValue,
} from '@/core/interfaces/common';
import { FormInput } from '@/core/components/FormInput';
import { FormSelect } from '@/core/components/FormSelect';
import { FormMultipleSelect } from '@/core/components/FormMultipleSelect';

import {
  fieldTypes,
} from '@/utils/constants';

interface CustomFormInputProps {
  backgroundcolor: string;
  isRequired?: boolean;
  error: () => string | false;
  customFieldData: CustomFieldWithValue;
  /**
   * Optional description of why the field exists or how it is used.
   * Always exposed to screen readers via aria-describedby; visually
   * revealed on demand through a small help toggle inside the field.
   */
  helpText?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onMultiChange: (values: Array<MultiOptionValue>) => void;
  onFocus: () => void;
  /**
   * Optional callback invoked when the browser fires the `invalid` event on the field.
   * Called after the native tooltip is suppressed via `event.preventDefault()`.
   * Use this to set a styled inline error message in place of the browser's native tooltip.
   */
  onInvalidField?: () => void;
  ariaLabel?: string;
}

export const CustomFormInput = ({
  backgroundcolor,
  customFieldData: {
    fieldType,
    options,
    label,
    uuid,
    value,
    values,
  },
  error,
  helpText,
  isRequired,
  onChange,
  onMultiChange,
  onFocus,
  onInvalidField,
  ariaLabel,
}: CustomFormInputProps) => {
  switch (fieldType) {
    case fieldTypes.textField:
      return (
        <FormInput
          ariaLabel={ariaLabel || label}
          backgroundcolor={backgroundcolor}
          error={error}
          helpText={helpText}
          isRequired={isRequired}
          name="customField"
          type="text"
          label={label}
          onChange={onChange}
          onFocus={onFocus}
          onInvalidField={onInvalidField}
          value={value}
        />
      );
    case fieldTypes.singleChoiceField:
      return (
        <FormSelect
          backgroundcolor={backgroundcolor}
          error={error}
          helpText={helpText}
          isRequired={isRequired}
          name={label}
          options={options}
          onChange={onChange}
          onFocus={onFocus}
          onInvalidField={onInvalidField}
          value={value}
        />
      );
    case fieldTypes.multipleChoiceField:
      return (
        <FormMultipleSelect
          ariaLabel={ariaLabel || label}
          backgroundcolor={backgroundcolor}
          error={error}
          helpText={helpText}
          isRequired={isRequired}
          name={label}
          options={options}
          onMultiChange={onMultiChange}
          onFocus={onFocus}
          onInvalidField={onInvalidField}
          values={values}
        />
      );
    default:
      return <></>;
  }
};

CustomFormInput.defaultProps = {
  helpText: '',
  isRequired: false,
};
